import React, { useState, useCallback } from "react";
import { useIntl, defineMessages } from "react-intl";
import PropTypes from "prop-types";
import ImageLightbox from "react-image-lightbox";
import { makeImageSrc } from "theme/components/atoms/Image";
import "react-image-lightbox/style.css";

const buildNavigation = (mainImageSrc, imagesSrc) => {
  const imagesLength = imagesSrc.length;
  if (imagesLength === 1) {
    return { main: mainImageSrc };
  }
  const mainImageIndex = imagesSrc.indexOf(mainImageSrc);
  const next = imagesSrc[(mainImageIndex + 1) % imagesLength];
  const previous =
    imagesSrc[(mainImageIndex + imagesLength - 1) % imagesLength];

  return {
    main: mainImageSrc,
    next,
    previous,
  };
};

const useLightboxNavigation = (mainImageSrc, imagesSrc) => {
  const [navigation, setNavigation] = useState(
    buildNavigation(mainImageSrc, imagesSrc)
  );

  const next = navigation.next;
  const moveToNext = useCallback(() => {
    next && setNavigation(buildNavigation(next, imagesSrc));
  }, [next, imagesSrc]);

  const previous = navigation.previous;
  const moveToPrevious = useCallback(() => {
    previous && setNavigation(buildNavigation(previous, imagesSrc));
  }, [previous, imagesSrc]);

  return { navigation, moveToNext, moveToPrevious };
};

const messages = defineMessages({
  nextLabel: {
    id: "components.organisms.Lightbox.nextLabel",
    defaultMessage: "Next image",
  },
  previousLabel: {
    id: "components.organisms.Lightbox.previousLabel",
    defaultMessage: "Previous image",
  },
  zoomInLabel: {
    id: "components.organisms.Lightbox.zoomInLabel",
    defaultMessage: "Zoom in",
  },
  zoomOutLabel: {
    id: "components.organisms.Lightbox.zoomOutLabel",
    defaultMessage: "Zoom out",
  },
  closeLabel: {
    id: "components.organisms.Lightbox.closeLabel",
    defaultMessage: "Close",
  },
});

const Lightbox = ({
  mainImageSrc,
  imagesSrc = [],
  imageFormat = "zoomable",
  onCloseRequest,
  onAfterOpen,
}) => {
  const intl = useIntl();
  const { navigation, moveToNext, moveToPrevious } = useLightboxNavigation(
    mainImageSrc,
    imagesSrc
  );

  return mainImageSrc ? (
    <ImageLightbox
      mainSrc={makeImageSrc(navigation.main, imageFormat)}
      nextSrc={
        navigation.next ? makeImageSrc(navigation.next, imageFormat) : null
      }
      prevSrc={
        navigation.previous
          ? makeImageSrc(navigation.previous, imageFormat)
          : null
      }
      onAfterOpen={onAfterOpen}
      onCloseRequest={onCloseRequest}
      onMoveNextRequest={moveToNext}
      onMovePrevRequest={moveToPrevious}
      nextLabel={intl.formatMessage(messages.nextLabel)}
      previousLabel={intl.formatMessage(messages.previousLabel)}
      zoomInLabel={intl.formatMessage(messages.zoomInLabel)}
      zoomOutLabel={intl.formatMessage(messages.zoomOutLabel)}
      closeLabel={intl.formatMessage(messages.closeLabel)}
    />
  ) : null;
};

Lightbox.propTypes = {
  mainImageSrc: PropTypes.string,
  imagesSrc: PropTypes.arrayOf(PropTypes.string),
  imageFormat: PropTypes.string,
  onCloseRequest: PropTypes.func.isRequired,
  onAfterOpen: PropTypes.func,
};

export default Lightbox;
